import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../store/useStore';
import { stileNuovo } from '../../navigazione/paginaIniziale';
import { messaggioDa } from '../../utils/notifiche';
import { annotaNavigazione, installaRegistro, voci, senzaQuery } from '../../utils/registroTecnico';
import { copiaPagina } from '../../utils/copiaPagina';
import { segretoAVideo } from '../../utils/versioneInLinea';
import '../../styles/PulsanteSegnalazione.css';

/**
 * PulsanteSegnalazione — «Segnala un problema», in basso a destra in ogni
 * schermata del gestionale (migration 346, proposta del 28/10/2026).
 *
 * DOVE C'E', e dove no. E' montato dai due gusci (Layout e LayoutClassico), e
 * quindi c'e' solo dove c'e' il menu: NON sul tablet, che e' in mano a un
 * paziente (un pulsante «segnala» manderebbe la copia di una pratica in corso),
 * non al telefono dello studio, non al login. Lo prova
 * components/guscio/pulsanteSegnalazione.test.js.
 *
 * SPENTO DI SERIE. Chiede al server se il modulo e' acceso (GET
 * /segnalazioni/stato, nessun dato e nessuna traccia nel registro degli
 * accessi); spento, o se la rotta non risponde, non disegna niente.
 *
 * Sta a z-index 890: sotto l'angolo degli avvisi (900) e sotto i veli delle
 * finestre (910 e oltre), cosi' non copre i loro pulsanti; a sinistra del
 * pulsante dell'assistente.
 */
const MAX_TESTO = 500;
const MSG_NON_RIUSCITA = 'La segnalazione non è partita: riprova fra un momento.';
/* Con un segreto a video (useTrattieniVersione: la chiave di un tablet, i
   codici di recupero, il PIN) la copia della pagina non si fa: finirebbe in
   un file che resta trenta giorni sul server (revisione del 29/10). */
export const MSG_SEGRETO_A_VIDEO = 'A schermo c\'è un codice da non copiare: la copia della pagina non si allega.';

/** Il nome del bundle in linea, dal <script> della pagina. */
function versioneInLinea() {
    const s = Array.from(document.querySelectorAll('script[src]'))
        .map((x) => (x.getAttribute('src') || '').split('/').pop())
        .find((n) => /^main\.[a-f0-9]+\.js$/.test(n));
    return s || null;
}

export default function PulsanteSegnalazione() {
    const utente = useAuthStore((s) => s.user);
    const location = useLocation();
    const [attivo, setAttivo] = useState(false);
    const [aperta, setAperta] = useState(false);
    const [testo, setTesto] = useState('');
    const [conCopia, setConCopia] = useState(true);
    const [invio, setInvio] = useState(false);
    const [esito, setEsito] = useState(null);
    const [errore, setErrore] = useState(null);
    const campo = useRef(null);

    useEffect(() => { installaRegistro(); }, []);
    useEffect(() => { annotaNavigazione(location.pathname); }, [location.pathname]);
    useEffect(() => {
        let vivo = true;
        api.get('/segnalazioni/stato')
            .then((r) => { if (vivo) setAttivo(r?.data?.data?.attivo === true); })
            .catch(() => { if (vivo) setAttivo(false); });
        return () => { vivo = false; };
    }, []);
    useEffect(() => { if (aperta && campo.current) campo.current.focus(); }, [aperta]);

    if (!attivo) return null;
    const segreto = aperta && segretoAVideo();

    const chiudi = () => { setAperta(false); setTesto(''); setConCopia(true); setEsito(null); setErrore(null); };

    const invia = async () => {
        setInvio(true);
        setErrore(null);
        try {
            /* La copia si fa PRIMA di tutto il resto, con la finestra della
               segnalazione tolta (data-segnalazione). */
            let copia = null;
            if (conCopia && !segretoAVideo()) {
                try { copia = copiaPagina(document); } catch (e) { copia = null; }
            }
            const r = await api.post('/segnalazioni', {
                percorso: senzaQuery(location.pathname),
                versione: versioneInLinea(),
                testo: testo.trim() || null,
                registro: {
                    voci: voci(),
                    finestra: { l: window.innerWidth, a: window.innerHeight },
                    stile: stileNuovo(utente) ? 'console' : 'classico',
                },
                copia,
            });
            setEsito({ codice: r.data.data.codice, messaggio: r.data.message, copiaRifiutata: r.data.data.copia_rifiutata });
        } catch (e) {
            setErrore(messaggioDa(e, MSG_NON_RIUSCITA));
        } finally {
            setInvio(false);
        }
    };

    return (
        <>
            <button type="button" className="seg-pulsante" data-segnalazione=""
                onClick={() => setAperta(true)} aria-label="Segnala un problema" title="Segnala un problema">
                <Bug size={18} aria-hidden="true" />
            </button>
            {aperta && (
                <div className="seg-velo" data-segnalazione="" onMouseDown={(e) => { if (e.target === e.currentTarget && !invio) chiudi(); }}>
                    <div className="seg-finestra" role="dialog" aria-modal="true" aria-labelledby="seg-titolo">
                        <h2 id="seg-titolo" className="seg-titolo">Segnala un problema</h2>
                        {esito ? (
                            <>
                                <p className="seg-esito">{esito.messaggio}</p>
                                <p className="seg-codice"><code>{esito.codice}</code></p>
                                {esito.copiaRifiutata && (
                                    <p className="seg-nota">La copia della pagina non è stata allegata.</p>
                                )}
                                <div className="seg-azioni">
                                    <button type="button" className="seg-secondario"
                                        onClick={() => { try { navigator.clipboard?.writeText(esito.codice); } catch (e) { /* niente */ } }}>
                                        Copia il codice
                                    </button>
                                    <button type="button" className="seg-primario" onClick={chiudi}>Chiudi</button>
                                </div>
                            </>
                        ) : (
                            <>
                                <label className="seg-etichetta" htmlFor="seg-testo">Che cosa stavi facendo? (facoltativo)</label>
                                <textarea id="seg-testo" ref={campo} className="seg-testo" maxLength={MAX_TESTO} rows={4}
                                    value={testo} onChange={(e) => setTesto(e.target.value)} disabled={invio} />
                                <div className="seg-conta">{testo.length}/{MAX_TESTO}</div>
                                <label className="seg-scelta">
                                    <input type="checkbox" checked={conCopia && !segreto} onChange={(e) => setConCopia(e.target.checked)}
                                        disabled={invio || segreto} />
                                    Allega una copia della pagina
                                </label>
                                {segreto && <p className="seg-nota">{MSG_SEGRETO_A_VIDEO}</p>}
                                {errore && <p className="seg-errore" role="alert">{errore}</p>}
                                <div className="seg-azioni">
                                    <button type="button" className="seg-secondario" onClick={chiudi} disabled={invio}>Annulla</button>
                                    <button type="button" className="seg-primario" onClick={invia} disabled={invio}>
                                        {invio ? 'Invio…' : 'Invia'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
